/**
 * @module Library
 *
 * GrowthBook feature flags, used to A/B test the web paywalls' content.
 */
// Types only: the SDK itself is required in createGrowthBook.
import { Attributes, GrowthBook } from "@growthbook/growthbook";
import {
  autoAttributesPlugin,
  growthbookTrackingPlugin,
} from "@growthbook/growthbook/plugins";
import { AnalyticsService, AuthService } from "app/services";
import config from "config/environment";

const API_HOST = "https://cdn.growthbook.io";
const INGESTOR_HOST = "https://us-east-1.gb-ingest.com";
// A paywall waits this long at most for the flags before it shows its defaults.
const INIT_TIMEOUT = 2000;

export interface GrowthBookUser {
  id?: string;
  kind?: string;
  sport?: string;
}

let growthbook: GrowthBook | null = null;
let loading: Promise<void> | null = null;
let loaded = false;
let userAttributes: Attributes = {};
let pageAttributes: Attributes = {};

const attributes = (): Attributes => ({ ...userAttributes, ...pageAttributes });

const createGrowthBook = (): GrowthBook => {
  // Required here, not imported: the SDK starts watching the DOM as soon as
  // it is evaluated, so it is only evaluated once the flags are wanted, not
  // by everything that imports this module (Jest's DOM can't be watched).
  // tslint:disable-next-line:no-var-requires
  const sdk: { GrowthBook: typeof GrowthBook } = require("@growthbook/growthbook");
  // tslint:disable-next-line:no-var-requires
  const plugins: {
    autoAttributesPlugin: typeof autoAttributesPlugin;
    growthbookTrackingPlugin: typeof growthbookTrackingPlugin;
  } = require("@growthbook/growthbook/plugins");

  return new sdk.GrowthBook({
    apiHost: API_HOST,
    clientKey: config.GROWTHBOOK_CLIENT_KEY,
    // Lets the GrowthBook DevTools extension force a variant for QA; what it
    // forces only changes the page it runs on.
    enableDevMode: true,
    attributes: attributes(),
    plugins: [
      // The browser's anonymous id goes under deviceId: under the default `id`
      // it would replace the user's id on every route change.
      plugins.autoAttributesPlugin({ uuidKey: "deviceId" }),
      plugins.growthbookTrackingPlugin({ ingestorHost: INGESTOR_HOST }),
    ],
    // The tracking plugin records the exposure in GrowthBook; this sends the
    // same exposure to Segment, once Segment knows who the user is: the auth
    // user settles after AuthService identifies them, and Segment keeps the
    // order of its calls.
    trackingCallback: (experiment, result) => {
      const send = () =>
        AnalyticsService.track("Experiment Viewed", {
          "Experiment ID": experiment.key,
          "Variation ID": result.key,
        });
      const Auth: any = AuthService;
      Auth.getAuthUser().then(send, send);
    },
  });
};

/**
 * Loads the flags, once. Resolves when they are in or after INIT_TIMEOUT,
 * whichever comes first; never rejects, so a GrowthBook outage only means
 * the defaults show.
 */
export function loadGrowthBook(): Promise<void> {
  if (loading == null) {
    loading = Promise.resolve()
      .then(() => {
        growthbook = createGrowthBook();
        return growthbook.init({ timeout: INIT_TIMEOUT });
      })
      .then(
        () => undefined,
        () => undefined,
      )
      .then(() => {
        loaded = true;
      });
  }
  return loading;
}

export const isGrowthBookLoaded = () => loaded;

/**
 * Sets who GrowthBook assigns variations to. `user_id` must be the id the
 * server sends Segment as userId: GrowthBook joins paywall exposures to the
 * trial and payment events on it. Attributes are visible in the browser, so
 * no email, name or other personal data.
 */
export function identifyGrowthBookUser(user?: GrowthBookUser | null) {
  if (user?.id == null) {
    return;
  }
  userAttributes = {
    id: user.id,
    user_id: user.id,
    kind: user.kind,
    sport: user.sport,
  };
  growthbook?.updateAttributes(attributes());
}

/**
 * Sets what growth can target about the paywall being read, e.g.
 * `billing_term_in_url`. Each paywall sets its own right before it reads its
 * flag, replacing the last one's.
 */
export function setGrowthBookPageAttributes(page: Attributes) {
  pageAttributes = page;
  growthbook?.updateAttributes(attributes());
}

/**
 * A flag's value, or `fallback` before the flags load. Reading a flag that
 * runs an experiment is what counts the user as exposed to it.
 */
export function getGrowthBookValue<T>(key: string, fallback: T): T {
  return growthbook != null
    ? (growthbook.getFeatureValue(key, fallback as any) as T)
    : fallback;
}
